import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

import { DataPanel, DataPanelItem, DebugView } from '@/ui';
import { DateFormatter } from '@/features/shared/DateFormatter';
import { internetConnectionLabel } from '@/signalr';
import type { Caller } from '@/signalr';

type DataSources = Caller['dataSources'];

export type CallerDataSourcesPanelProps = {
  dataSources: DataSources;
  snapshotRevision?: number;
};

type SourceEntryProps = {
  title: string;
  sourceId: string;
  children?: ReactNode;
};

function SourceEntry({ title, sourceId, children }: SourceEntryProps) {
  return (
    <DataPanelItem title={title} valueToCopy={sourceId} allowCopyToClipboard>
      {children}
      <div className="small text-muted text-break">
        ID: <code>{sourceId}</code>
      </div>
    </DataPanelItem>
  );
}

function joined(values: readonly (string | null | undefined)[]): string | undefined {
  const nonEmpty = values.filter((value): value is string => value != null && value.trim() !== '');
  return nonEmpty.length === 0 ? undefined : nonEmpty.join(' · ');
}

export function CallerDataSourcesPanel({ dataSources, snapshotRevision }: CallerDataSourcesPanelProps) {
  const { t, i18n } = useTranslation();
  const numberFormat = new Intl.NumberFormat(i18n.language || 'en', { maximumFractionDigits: 0 });

  return (
    <DebugView>
      <DataPanel header={t('Incident_InfoPanel_DataSources')}>
        {snapshotRevision != null && <DataPanelItem title="Snapshot revision" value={String(snapshotRevision)} />}

        {dataSources.mobileApp.map((source) => (
          <SourceEntry key={source.sourceId} title={t('Incident_InfoPanel_MobileAppName')} sourceId={source.sourceId}>
            <div>
              {joined([source.appInfo.code, source.appInfo.version, source.appInfo.language, source.transport])}
            </div>
          </SourceEntry>
        ))}

        {dataSources.mobileAppSms.map((source) => (
          <SourceEntry key={source.sourceId} title={t('Incident_InfoPanel_MobileAppSms')} sourceId={source.sourceId}>
            <div>
              {t('Incident_InfoPanel_SentAt')}: <DateFormatter date={source.sentAtUtc} formatType="calendar" />
            </div>
          </SourceEntry>
        ))}

        {dataSources.webApp.map((source) => (
          <SourceEntry key={source.sourceId} title="WebApp" sourceId={source.sourceId}>
            {source.deviceInfo != null && (
              <div>
                {joined([
                  source.deviceInfo.system,
                  source.deviceInfo.systemVersion,
                  source.deviceInfo.batteryLevel == null
                    ? null
                    : `${numberFormat.format(source.deviceInfo.batteryLevel)} %`,
                  internetConnectionLabel(source.deviceInfo.internetConnectionType),
                  source.deviceInfo.userAgent,
                ])}
              </div>
            )}
          </SourceEntry>
        ))}

        {dataSources.appleEed.map((source) => {
          const capabilities = [
            `${t('Incident_InfoPanel_Capability_LiveVideo')}: ${t(source.capabilities.liveVideo ? 'Global_Yes' : 'Global_No')}`,
            `${t('Incident_InfoPanel_Capability_MediaUpload')}: ${t(source.capabilities.mediaUpload ? 'Global_Yes' : 'Global_No')}`,
            `${t('Incident_InfoPanel_Capability_AdditionalData')}: ${t(source.capabilities.additionalData ? 'Global_Yes' : 'Global_No')}`,
          ];

          return (
            <SourceEntry key={source.sourceId} title="Apple EED" sourceId={source.sourceId}>
              <div>{joined(capabilities)}</div>
              <div className="small">
                {t('Incident_InfoPanel_LocationReceived')}: {t(source.receivedLocation ? 'Global_Yes' : 'Global_No')}
              </div>
            </SourceEntry>
          );
        })}

        {dataSources.googleEls.map((source) => (
          <SourceEntry key={source.sourceId} title="Google ELS" sourceId={source.sourceId}>
            {source.emergencyType != null && (
              <div>
                {t('Incident_InfoPanel_EmergencyType')}: {source.emergencyType}
              </div>
            )}
          </SourceEntry>
        ))}

        {dataSources.aml.map((source) => (
          <SourceEntry key={source.sourceId} title="AML" sourceId={source.sourceId} />
        ))}
      </DataPanel>
    </DebugView>
  );
}
